import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useUnreadMessages = (userId: string | undefined, excludeSystem = false) => {
    const queryClient = useQueryClient();

    const { data: unreadCount = 0 } = useQuery({
        queryKey: ["unread-messages-count", userId, excludeSystem],
        queryFn: async () => {
            if (!userId) return 0;

            let query = supabase
                .from("messages")
                .select("*", { count: "exact", head: true })
                .eq("is_read", false)
                .or(`sender_id.neq.${userId},sender_id.is.null`);

            if (excludeSystem) {
                // Filter out system notifications if requested
                query = query.not("conversation_id", "ilike", "system_notifications_%");
            }

            const { count, error } = await query;

            if (error) {
                console.error("Error fetching unread count:", error);
                return 0;
            }

            return count || 0;
        },
        enabled: !!userId,
        staleTime: 0, // Always refetch on invalidation
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!userId) return;

        // Listen to real-time changes on the messages table to update the badge immediately
        const channel = supabase
            .channel("unread-messages-updates")
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "messages",
                },
                () => {
                    // Use a broad invalidation to ensure all variations (excludeSystem true/false) are updated
                    queryClient.invalidateQueries({
                        queryKey: ["unread-messages-count", userId],
                        exact: false
                    });
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [userId, queryClient]);

    return unreadCount;
};
